// CreaSfida.tsx — «Sfida qualcuno»: il nome, poi il server crea il link e si entra nella stanza ad aspettare.
// In Base si vede quante partite restano oggi; finita la quota: video con premio (app), Silver, o domani.
import { AvvisoSospensione } from "../componenti/AvvisoSospensione.tsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CampoNome } from "./CampoNome.tsx";
import { creaSfida, salvaToken } from "../servizi/sfide.ts";
import { ErroreRete } from "../servizi/api.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { vai } from "../servizi/percorso.ts";
import { avatarAttuale, leggiQuota, useAccount, type Quota } from "../servizi/account.ts";
import { premioDisponibile } from "../servizi/pubblicita.ts";
import { PERCORSO_PIANI, PERCORSO_SFIDA, PREFERENZE } from "../costanti.ts";

const ERRORI_NOTI = ["troppe_sfide", "sospeso", "gioco_spento", "offline", "timeout", "troppe_richieste", "bloccato", "quota_finita", "nome_vietato"];

export function CreaSfida({ onAnnulla }: { onAnnulla: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [nome, setNome] = useState(() => (account.tipo === "registrato" ? account.io.nome : leggiPreferenza(PREFERENZE.nome)) ?? "");
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [quotaLetta, setQuotaLetta] = useState(false);

  // Il profilo può arrivare dopo l'apertura della finestra: il nome dell'account si propone appena c'è.
  const nomeAccount = account.tipo === "registrato" ? account.io.nome : null;
  useEffect(() => {
    if (nomeAccount) setNome((n) => n || nomeAccount);
  }, [nomeAccount]);

  useEffect(() => {
    leggiQuota()
      .then(setQuota)
      .catch(() => setQuota(null)) // senza quota si prova lo stesso: decide il server
      .finally(() => setQuotaLetta(true));
  }, []);
  const finita = errore === "quota_finita" || (quota !== null && !quota.illimitate && quota.restanti <= 0);

  const crea = (n: string) => {
    salvaPreferenza(PREFERENZE.nome, n);
    setInvio(true);
    setErrore(null);
    creaSfida(n, avatarAttuale())
      .then((s) => {
        salvaToken(s.codice, s.token);
        vai(`${PERCORSO_SFIDA}${s.codice}`);
      })
      .catch((e: unknown) => {
        const c = e instanceof ErroreRete ? e.codice : "server";
        setErrore(ERRORI_NOTI.includes(c) ? c : "generico");
        setInvio(false);
      });
  };
  // v037: «il più semplice possibile» — se il nome c'è già (account o ultima volta), il link si crea subito: niente da
  // scrivere né da toccare. Il nome si cambia dal profilo (o qui, se qualcosa va storto).
  const diretta = useRef(false);
  const nomeNoto = (account.tipo === "registrato" ? account.io.nome : leggiPreferenza(PREFERENZE.nome)) ?? "";
  useEffect(() => {
    if (diretta.current || !nomeNoto.trim() || !quotaLetta || finita) return;
    diretta.current = true;
    crea(nomeNoto.trim());
  }, [quotaLetta, finita]); // eslint-disable-line react-hooks/exhaustive-deps

  if (finita)
    return (
      <div className="crea-sfida">
        <p className="pannello-titolo">{t("quota.finita_titolo")}</p>
        <p className="nota">{t("quota.finita_testo", { limite: quota?.limite ?? 0 })}</p>
        <div className="azioni-colonna">
          {quota && quota.premiRestanti > 0 && premioDisponibile() && (
            <p className="nota">{t("quota.premio_app")}</p>
          )}
          <button type="button" className="pulsante pulsante-primario" onClick={() => vai(PERCORSO_PIANI)}>
            {t("piani.passa_silver")}
          </button>
          <button type="button" className="pulsante pulsante-secondario" onClick={onAnnulla}>
            {t("quota.domani")}
          </button>
        </div>
      </div>
    );

  // il link si sta creando da solo (nome già noto): niente modulo da riempire, solo l'attesa
  if (nomeNoto.trim() && !errore)
    return (
      <div className="crea-sfida" role="status" aria-busy="true">
        <p className="nota-centrata">{t("gioco.creazione")}</p>
      </div>
    );

  return (
    <form
      className="crea-sfida"
      onSubmit={(e) => {
        e.preventDefault();
        const n = nome.trim();
        if (!n) return;
        crea(n);
      }}
    >
      <p className="nota">{t("gioco.crea_testo")}</p>
      <CampoNome valore={nome} onCambia={setNome} />
      {quota && !quota.illimitate && (
        <p className="nota" data-quota={quota.restanti}>
          {t("quota.restanti", { restanti: quota.restanti, limite: quota.limite })}
        </p>
      )}
      {errore === "sospeso" ? (
        <AvvisoSospensione />
      ) : errore && (
        <p className="admin-errore" role="alert">
          {t(`gioco.errore_${errore}`, { defaultValue: t("gioco.errore_generico") })}
        </p>
      )}
      <div className="azioni-riga">
        <button type="button" className="pulsante pulsante-secondario" onClick={onAnnulla}>
          {t("generale.annulla")}
        </button>
        <button type="submit" className="pulsante pulsante-primario" disabled={invio}>
          {invio ? t("gioco.creazione") : t("gioco.crea")}
        </button>
      </div>
    </form>
  );
}
